import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { ImageInput } from "@/components/ImageInput";

// Multiple-photo editor: thumbnails with remove + one ImageInput to append.
const MultiImageInput = ({ value, onChange, testid }) => {
  const list = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-3" data-testid={testid}>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {list.map((url, i) => (
            <div key={i} className="relative">
              <img src={url} alt="" className="h-16 w-16 rounded-xl object-cover" />
              <button
                type="button"
                onClick={() => onChange(list.filter((_, j) => j !== i))}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white"
                data-testid={`${testid}-remove-${i}`}
                aria-label="Remove photo"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
      <ImageInput value="" onChange={(url) => url && onChange([...list, url])} testid={`${testid}-add`} />
    </div>
  );
};

/** Generic brand-scoped CRUD for a Docstate collection (listings, agents). */
export const ReCollectionTab = ({ endpoint, cfg }) => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/${endpoint}`);
      setItems(data);
    } catch {
      setItems([]);
    }
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    const f = {};
    cfg.fields.forEach((fd) => {
      f[fd.name] =
        fd.type === "boolean" ? "true" : fd.type === "images" ? [] : fd.type === "select" ? (fd.options[0].value ?? fd.options[0]) : "";
    });
    setForm(f);
    setEditing("new");
  };

  const openEdit = (item) => {
    const f = {};
    cfg.fields.forEach((fd) => {
      const v = item[fd.name];
      if (fd.type === "list") f[fd.name] = (v || []).join(", ");
      else if (fd.type === "images") f[fd.name] = v || [];
      else if (fd.type === "boolean") f[fd.name] = v === false ? "false" : "true";
      else f[fd.name] = v ?? "";
    });
    setForm(f);
    setEditing(item);
  };

  const save = async () => {
    setSaving(true);
    const payload = {};
    cfg.fields.forEach((fd) => {
      let v = form[fd.name];
      if (fd.type === "list") v = String(v || "").split(",").map((x) => x.trim()).filter(Boolean);
      else if (fd.type === "images") v = Array.isArray(v) ? v : [];
      else if (fd.type === "boolean") v = v === "true" || v === true;
      else if (fd.type === "number") v = v === "" ? 0 : Number(v);
      payload[fd.name] = v;
    });
    if (cfg.beforeSave) cfg.beforeSave(payload);
    try {
      if (editing === "new") await api.post(`/admin/${endpoint}`, payload);
      else await api.put(`/admin/${endpoint}/${editing.id}`, payload);
      await load();
      toast.success(`${cfg.singular} saved`);
      setEditing(null);
    } catch {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${cfg.title(item)}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/${endpoint}/${item.id}`);
      await load();
      toast.success(`${cfg.singular} deleted`);
    } catch {
      toast.error("Delete failed");
    }
  };

  const key = endpoint.split("/").pop();

  if (items === null)
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid={`re-${key}-loading`}>
        <Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" aria-label="Loading" />
      </div>
    );

  return (
    <div data-testid={`re-${key}-tab`}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">{cfg.heading}</h2>
          <p className="mt-1 text-xs text-[#91837C]">{items.length} {items.length === 1 ? cfg.singular.toLowerCase() : `${cfg.singular.toLowerCase()}s`}</p>
        </div>
        <button onClick={openNew} data-testid={`re-${key}-add-button`} className="btn-gold px-5 py-2.5 text-xs">
          <Plus className="h-4 w-4" aria-hidden="true" /> Add {cfg.singular}
        </button>
      </div>

      <div className="mt-5 space-y-3">
        {items.map((item) => (
          <div key={item.id} className="glass flex items-center gap-4 rounded-2xl p-4" data-testid={`re-${key}-item-${item.id}`}>
            {(cfg.image?.(item)) && <img src={cfg.image(item)} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" loading="lazy" />}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[#1A1615]">{cfg.title(item)}</p>
              <p className="truncate text-xs text-[#91837C]">{cfg.subtitle(item)}</p>
            </div>
            {cfg.badge && cfg.badge(item)}
            <button onClick={() => openEdit(item)} data-testid={`re-${key}-edit-${item.id}`} aria-label="Edit"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#B38B2D] transition-colors hover:bg-[#D4AF37]/10">
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </button>
            <button onClick={() => remove(item)} data-testid={`re-${key}-delete-${item.id}`} aria-label="Delete"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-red-200 text-red-500 transition-colors hover:bg-red-50">
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">Nothing here yet — add your first {cfg.singular.toLowerCase()}.</p>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/60 bg-[#FFFDF9]/95 backdrop-blur-xl sm:max-w-xl" data-testid={`re-${key}-editor`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-semibold text-[#1A1615]">
              {editing === "new" ? `New ${cfg.singular}` : `Edit ${cfg.singular}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {cfg.fields.map((fd) => (
              <div key={fd.name}>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">{fd.label}</label>
                {fd.type === "textarea" ? (
                  <textarea rows={4} value={form[fd.name] || ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`re-field-${fd.name}`} className="input-lux resize-none" />
                ) : fd.type === "select" ? (
                  <select value={form[fd.name] ?? ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`re-field-${fd.name}`} className="input-lux">
                    {fd.options.map((o) => {
                      const val = o.value ?? o;
                      const lab = o.label ?? o;
                      return <option key={val} value={val}>{lab}</option>;
                    })}
                  </select>
                ) : fd.type === "boolean" ? (
                  <select value={form[fd.name] || "true"} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`re-field-${fd.name}`} className="input-lux">
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : fd.type === "image" ? (
                  <ImageInput value={form[fd.name] || ""} onChange={(v) => setForm({ ...form, [fd.name]: v })} testid={`re-field-${fd.name}`} />
                ) : fd.type === "images" ? (
                  <MultiImageInput value={form[fd.name]} onChange={(v) => setForm({ ...form, [fd.name]: v })} testid={`re-field-${fd.name}`} />
                ) : (
                  <input type={fd.type === "number" ? "number" : "text"} value={form[fd.name] || ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`re-field-${fd.name}`} className="input-lux" />
                )}
              </div>
            ))}
            <button onClick={save} disabled={saving} data-testid={`re-${key}-save-button`} className="btn-gold w-full px-6 py-3.5 text-sm disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
